import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { ResultsGallerySection } from "@/components/ResultsGallery";
import { IMG, SERVICES } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

const commercial = SERVICES.find((s) => s.id === "commercial");

const sectors = [
  { title: "Offices & Corporate", desc: "Reception areas, meeting rooms, workstations, staff kitchens and amenities — kept consistently presentable.", image: IMG.office },
  { title: "Retail & Shopfronts", desc: "Clean, welcoming spaces that make the right impression on every customer who walks through the door.", image: IMG.commercial },
  { title: "Facilities & Common Areas", desc: "Commercial buildings, shared spaces and high-traffic areas maintained on a schedule that suits your operations.", image: IMG.interior },
];

export default function Commercial() {
  useSeo("Commercial & Office Cleaning Perth | RD Cleaning Services", "Professional commercial cleaning for Perth businesses — offices, retail, medical facilities, warehouses and education facilities. Flexible schedules and reliable service.");
  return (
    <>
      <PageHero
        eyebrow="Commercial Cleaning Perth"
        title="Professional Commercial Cleaning for Perth Businesses"
        subtitle="A clean workplace creates a better environment for employees, customers and visitors. RD Cleaning Services provides flexible commercial cleaning solutions for offices, retail spaces, medical facilities, warehouses, education facilities and other commercial environments."
        image={IMG.commercial}
      >
        <Link to="/quote" className="focus-ring inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-navy-deep transition-all hover:-translate-y-0.5 hover:bg-teal-400" data-testid="commercial-hero-quote-button">
          Request a Commercial Quote <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Who We Work With</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Cleaning that works around your business.
            </h2>
            <p className="mt-4 leading-relaxed text-slate-600">
              Early mornings, evenings or weekends — we schedule cleaning around your operating hours so
              your business never misses a beat.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {sectors.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <article className="group h-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl" data-testid={`sector-card-${i}`}>
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={s.image} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-display text-lg font-bold text-navy">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-3xl shadow-xl">
              <img src={IMG.floor} alt="Professional cleaner mopping a hard floor in a commercial space" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow">What's Covered</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              A complete workplace cleaning service.
            </h2>
            <ul className="mt-7 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
              {commercial.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" /> {item}
                </li>
              ))}
            </ul>
            <Link to="/quote" className="focus-ring mt-9 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-700" data-testid="commercial-quote-button-2">
              Request a Commercial Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <ResultsGallerySection
        slugs={["retail-tenancy", "shopping-centre", "strata-lobby", "gym", "windows", "warehouse"]}
        heading="Before & After in Commercial Spaces"
        text="Retail, common areas, gyms and warehouses — the same space before and after our clean."
        bg="bg-white"
        testid="commercial-gallery-section"
      />

      <CtaSection heading="Let's Keep Your Workplace at Its Best" text="Tell us about your facility and operating hours, and we'll design a commercial cleaning plan that fits." />
    </>
  );
}
